import { Box, BoxProps, Stack, StackProps } from '@mui/material'
import { FC, PropsWithChildren, ReactNode, useState } from 'react'
import { useLocation } from '../../api/apiQueries'
import { LocationSearch } from '../LocationSearch'
import { ErrorScreen } from '../ErrorScreen'
import { Header } from './Header'
import { useLocationId } from '../../hooks/useLocationId'

/**
 * Props for the `Page` component.
 */
export type PageProps = PropsWithChildren<
  BoxProps & {
    /**
     * The title of the page, shown in a level 1 heading at the top. If not provided, no header will be shown.
     */
    title?: string
    /**
     * Whether to show the location search bar at the top of the page. Defaults to `true`.
     */
    showLocationSearchBar?: boolean
    /**
     * Whether to show the children when no location is provided in the URL. Defaults to `false`.
     */
    showChildrenWhenMissingLocation?: boolean
    /**
     * Whether to allow camera ID search. Defaults to `false`.
     */
    shouldAllowCameraIdSearch?: boolean
    /**
     * Additional content to display beside the location search bar
     */
    headerAddOn?: ReactNode
    /**
     * Additional content to display as a side panel
     */
    sidePanelAddOn?: ReactNode
    /**
     * Callback function to be called when the location ID changes due to a search attempt.
     *
     * Defaults to adding or replacing the `locationId` in the URL with the new one.
     *
     * @privateRemarks
     *
     * This function can be used to handle the location ID change or camera ID change in a custom way, such as updating the URL differently,
     * triggering a state change, or confirming unsaved changes, etc.
     *
     * @param locationId the new location ID in use.
     * @param cameraId the camera ID if the search was done via camera ID.
     */
    onSearch?: (locationId: string, cameraId?: string) => void

    scrollContainerProps?: StackProps

    maxWidthContainerProps?: BoxProps
  }
>

export const Page: FC<PageProps> = ({
  title,
  showLocationSearchBar: shouldShowLocationSearchBar = true,
  showChildrenWhenMissingLocation: shouldShowChildrenWhenMissingLocation = false,
  shouldAllowCameraIdSearch: shouldAllowCameraIdSearch = false,
  headerAddOn: headerAddOn,
  sidePanelAddOn: sidePanelAddOn,
  onSearch: onSearch,
  children,
  scrollContainerProps = {},
  maxWidthContainerProps = {},
  ...boxProps
}) => {
  const { locationId, setLocationId } = useLocationId()
  const locationQuery = useLocation(locationId)
  const [hasSystemNumberSearchError, setHasSystemNumberSearchError] = useState(false)

  // There's no reason to show the page header (or skeleton) if no title is provided
  const shouldShowPageHeader = title !== undefined

  /**
   * Handles a location ID search attempt by executing `onLocationIdChange` if provided, or by using the default
   * strategy of either inserting or replacing the location ID in the URL.
   *
   * @param locationId The location ID searched.
   * @param cameraId
   */
  const handleSearch = (locationId: string, cameraId?: string | undefined) => {
    setHasSystemNumberSearchError(false)

    if (cameraId && onSearch) {
      onSearch(locationId, cameraId)
      return
    }

    // Run a custom handler if provided, otherwise use the default one
    const handler: (locationId: string) => void = onSearch ?? setLocationId
    handler(locationId)
  }

  const { sx: scrollContainerSx = {}, ...restScrollContainerProps } = scrollContainerProps
  const { sx: maxWidthContainerSx = {}, ...restMaxWidthContainerProps } = maxWidthContainerProps
  const shouldShowNotFoundError = hasSystemNumberSearchError || (Boolean(locationId) && locationQuery.error?.status === 404)

  return (
    <Stack
      sx={{
        height: '100vh',
        width: '100%',
        overflow: 'auto',
        ...scrollContainerSx
      }}
      useFlexGap
      data-testid="page-container"
      {...restScrollContainerProps}
    >
      <Box sx={{ flex: 1, maxWidth: '1160px', ...maxWidthContainerSx }} {...restMaxWidthContainerProps}>
        {shouldShowPageHeader && (
          <Box sx={{ padding: title ? 'var(--spacing-m) var(--spacing-l)' : 0 }}>
            <Header headerText={title} data-testid="page-heading" />
          </Box>
        )}

        <Stack direction="row" sx={{ flexGrow: 1, overflow: 'hidden', height: '100%' }}>
          <Stack
            useFlexGap
            sx={{
              flexGrow: 1,
              width: '100%',
              paddingTop: shouldShowPageHeader ? '0' : 'var(--spacing-l)',
              paddingX: 'var(--spacing-l)',
              paddingBottom: 'var(--spacing-l)'
            }}
            spacing={'var(--spacing-l)'}
          >
            {shouldShowLocationSearchBar ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  gap: 'var(--spacing-m)',
                  marginTop: 0
                }}
              >
                <LocationSearch
                  onSearchChange={handleSearch}
                  shouldAllowCameraIdSearch={shouldAllowCameraIdSearch}
                  onError={error => {
                    setHasSystemNumberSearchError(!!error)
                    if (error) {
                      setLocationId(undefined, { clearUrlParams: true })
                    }
                  }}
                />
                {headerAddOn}
              </Stack>
            ) : (
              headerAddOn
            )}
            <Box data-testid={locationQuery.isLoading ? 'page-body-loading' : 'page-body'} {...boxProps}>
              {shouldShowNotFoundError ? (
                <ErrorScreen
                  data-testid="error-screen"
                  title="Oops, sorry this SID or System Number could not be found!"
                  message="A refresh may fix this issue."
                />
              ) : locationId ? (
                children
              ) : (
                shouldShowChildrenWhenMissingLocation && children
              )}
            </Box>
          </Stack>

          {sidePanelAddOn && <Box sx={{ height: '100%', flexShrink: 0 }}>{sidePanelAddOn}</Box>}
        </Stack>
      </Box>
    </Stack>
  )
}
